/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  Subject,
  Subscription,
  takeUntil,
} from "rxjs";

export type GlobalState = {
  [key in string]: string | object | number | null | undefined | {};
};
type getStateType<T extends GlobalState> = <K extends keyof T>(name: K) => Observable<T[K]>;

export class BLoCBase<T extends GlobalState = {}> {
  protected destroy$ = new Subject<void>();

  protected readonly state: BehaviorSubject<T>;
  private subs: { [key in string]: Subscription } = {};

  constructor(initState: T | null = null) {
    this.state = new BehaviorSubject<T>(initState || ({} as T));
  }

  /**
   * If you want to call subscribe manually from the BLoC component pass use addSub
   * It will ensure unsubscribe is called when BLoC gets cleaned up.
   *
   * @param sub RxJS Subscription object
   * @param sub Key for the subscription provided. If emtpy then random key will be used.
   *            This is used to cancel previous subscription on resubscribe
   */
  protected addSub = (sub: Subscription, key?: string) => {
    key && this.unsub(key);
    this.subs[key ?? Object.keys(this.subs).length] = sub;
  };
  protected shared$ = <T>($: Observable<T>) => $.pipe(takeUntil(this.destroy$), shareReplay(1));

  public onDestroyBase = () => {
    this.destroy$.next();
    this.destroy$.complete();
  };

  public unsub = (key?: string) => {
    if (!key) {
      Object.values(this.subs).forEach((s) => s.unsubscribe());
    } else if (key && this.subs[key]) {
      this.subs[key]?.unsubscribe();
    }
  };

  /**
   *
   * @param name Name of the state key that you would like to access
   * @returns An Observable that provide access to the key. This will only emit on distinct change.
   *          So when state is set if this key was not changed it will not emit.
   *          Note that if data type is not object (num,str,etc) observable will not re-emit until value has changed
   *          and object will emit if Ref has changed
   */
  protected readonly getState$: getStateType<T> = (name) =>
    this.state.pipe(
      map((a) => a[name]),
      distinctUntilChanged(),
      shareReplay(1),
    );

  /**
   *
   * @param mapper  a function that takes state and then allows you to create an observable from
   *                from a specific path
   * @returns Same as $getState
   */
  protected readonly mapState$ = (mapper: (s: T) => any) =>
    this.state.pipe(map(mapper), distinctUntilChanged());

  /**
   *
   * @param name Key of the state to change
   * @param value New value
   * @returns void
   */
  protected readonly setState = <K extends keyof T, Z extends T[K]>(name: K, value: Z): void =>
    this.state.next(
      Object.assign({}, this.state.value, {
        [name]: value && typeof value === "object" ? Object.freeze(value) : value, // ensure state is immutable
      }),
    );

  /**
   *
   * @param partialState Pass in the whole new state object (partial) and it will override any matching state
   * @returns void
   */
  protected readonly setStates = (partialState: Partial<T>): void =>
    this.state.next(Object.assign({}, this.state.value, partialState));

  /**
   *
   * @param name Key of the state to change
   * @param mutator Takes in a function that passes previous state and expects new state to be returned
   * @returns void
   */
  protected readonly mutateState = <K extends keyof T>(name: K, mutator: (prev: T[K]) => T[K]): void => {
    const prev = this.currentState(name);
    const next = mutator(prev);
    this.state.next(
      Object.assign({}, this.state.value, {
        [name]: next && typeof next === "object" ? Object.freeze(next) : next,
      }),
    );
  };

  // protected readonly currentState = <K extends keyof T>(name?: K) =>
  //   name !== null ? this.state.value[name] : this.state.value;
  /**
   * Gets current value as of this moment.
   * Do not expose outside of BLoC
   *
   * @param name Name of the state key that you would like to access
   * @returns State
   */
  protected currentState(): T;
  protected currentState<K extends keyof T>(name: K): T[K];
  protected currentState<K extends keyof T>(name?: K): T | T[K] {
    return name ? this.state.value[name] : this.state.value;
  }
}
